<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/composables/useToast';
import InboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsSectionCard from 'dashboard/components-next/Settings/SettingsSectionCard.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

const templates = ref([]);
const isLoading = ref(false);
const isSyncing = ref(false);
const isCreating = ref(false);
const showCreateModal = ref(false);
const editingId = ref(null);
const viewTemplate = ref(null);
const deleteConfirmName = ref(null);
const usageData = ref(null);
const isLoadingUsage = ref(false);
const usagePeriod = ref(30);
const isUploadingMedia = ref(false);

const LANGUAGES = [
  { code: 'pt_BR', label: 'Portugues (BR)' },
  { code: 'en', label: 'English' },
  { code: 'en_US', label: 'English (US)' },
  { code: 'es', label: 'Espanol' },
  { code: 'fr', label: 'Francais' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
];

// Meta pricing per message (USD) — Brazil, as of 2024
const TEMPLATE_COSTS = {
  MARKETING: 0.0625,
  UTILITY: 0.008,
  AUTHENTICATION: 0.0315,
  SERVICE: 0.0,
};

const PERIOD_OPTIONS = [7, 30, 90];

const LIMITS = { header: 60, body: 1024, footer: 60, button: 25 };

const HEADER_FORMATS = [
  {
    value: 'NONE',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_FORMAT_NONE',
  },
  {
    value: 'TEXT',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_FORMAT_TEXT',
  },
  {
    value: 'IMAGE',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_FORMAT_IMAGE',
  },
];

const BUTTON_TYPES = [
  {
    value: 'QUICK_REPLY',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.QUICK_REPLY',
  },
  { value: 'URL', label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.URL_TYPE' },
  {
    value: 'PHONE_NUMBER',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.PHONE_TYPE',
  },
];

const TEMPLATE_TYPES = [
  {
    value: 'STANDARD',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.TYPE_STANDARD',
    icon: 'i-lucide-message-square',
  },
  {
    value: 'CAROUSEL',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.TYPE_CAROUSEL',
    icon: 'i-lucide-gallery-horizontal-end',
  },
  {
    value: 'LTO',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.TYPE_LTO',
    icon: 'i-lucide-badge-percent',
  },
  {
    value: 'AUTH',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.TYPE_AUTH',
    icon: 'i-lucide-key-round',
  },
];

const CAROUSEL_LIMITS = { cards: 10, minCards: 2, cardBody: 160, buttons: 2 };
const LTO_OFFER_LIMIT = 16;

// Modelos prontos para a galeria
const STARTER_TEMPLATES = [
  {
    key: 'welcome',
    name: 'boas_vindas',
    category: 'UTILITY',
    headerFormat: 'NONE',
    header: '',
    body: 'Olá {{1}}! 👋 Seja bem-vindo(a) à {{2}}. Como podemos te ajudar hoje?',
    footer: '',
    buttons: [
      { type: 'QUICK_REPLY', text: 'Falar com atendente', url: '', phone: '' },
    ],
    variableExamples: ['Maria', 'Smart2'],
  },
  {
    key: 'promo',
    name: 'oferta_especial',
    category: 'MARKETING',
    headerFormat: 'TEXT',
    header: 'Oferta especial 🔥',
    body: 'Oi {{1}}! Separamos uma oferta exclusiva pra você. Quer dar uma olhada?',
    footer: 'Responda PARAR para não receber',
    buttons: [
      { type: 'QUICK_REPLY', text: 'Quero ver', url: '', phone: '' },
      { type: 'QUICK_REPLY', text: 'Agora não', url: '', phone: '' },
    ],
    variableExamples: ['cliente'],
  },
  {
    key: 'reminder',
    name: 'lembrete_agendamento',
    category: 'UTILITY',
    headerFormat: 'NONE',
    header: '',
    body: 'Olá {{1}}, lembrete do seu agendamento em {{2}} às {{3}}. Você confirma a presença?',
    footer: '',
    buttons: [
      { type: 'QUICK_REPLY', text: 'Confirmar', url: '', phone: '' },
      { type: 'QUICK_REPLY', text: 'Remarcar', url: '', phone: '' },
    ],
    variableExamples: ['Maria', '25/06', '14h'],
  },
];

const form = reactive({
  name: '',
  category: 'MARKETING',
  language: 'pt_BR',
  templateType: 'STANDARD',
  headerFormat: 'NONE',
  header: '',
  headerHandle: '',
  headerImagePreview: '',
  headerExample: '',
  body: '',
  footer: '',
  buttons: [],
  variableExamples: [],
  // Carrossel: cards (imagem + texto) + botões compartilhados por todos os cards.
  cards: [],
  carouselButtons: [],
  // Oferta por tempo limitado (LTO): exige cupom (COPY_CODE) + botão de URL.
  lto: {
    text: 'Oferta por tempo limitado',
    hasExpiration: true,
    code: '',
    urlText: '',
    url: '',
  },
  // Autenticação (OTP): código de expiração + recomendação de segurança.
  auth: {
    codeExpirationMinutes: 5,
    addSecurityRecommendation: true,
  },
});

const isCarousel = computed(() => form.templateType === 'CAROUSEL');
const isLto = computed(() => form.templateType === 'LTO');
const isAuth = computed(() => form.templateType === 'AUTH');
const isStandard = computed(() => form.templateType === 'STANDARD');

const formErrors = reactive({ name: '', body: '', general: '' });

const bodyTextarea = ref(null);
const showEmojiPicker = ref(false);

const COMMON_EMOJIS = [
  '😊',
  '👋',
  '🎉',
  '✅',
  '❤️',
  '🔥',
  '⭐',
  '💰',
  '📦',
  '🛒',
  '📱',
  '💬',
  '🎁',
  '👍',
  '🚀',
  '⏰',
  '📣',
  '🏷️',
  '💡',
  '🔔',
  '✨',
  '💪',
  '🙏',
  '📞',
];

const bodyCharCount = computed(() => form.body.length);
const headerCharCount = computed(() => form.header.length);
const footerCharCount = computed(() => form.footer.length);

// ── Variáveis ────────────────────────────────────────────────────────────
const detectVariables = text => {
  const found = [...String(text || '').matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map(
    m => parseInt(m[1], 10)
  );
  return [...new Set(found)].sort((a, b) => a - b);
};

const detectedVariables = computed(() => detectVariables(form.body));
// Header de texto aceita no máximo 1 variável ({{1}}).
const headerHasVariable = computed(
  () => form.headerFormat === 'TEXT' && detectVariables(form.header).length > 0
);

const varLabel = v => `{{${v}}}`;

// ── Editor helpers ───────────────────────────────────────────────────────
const wrapSelection = (before, after) => {
  const textarea = bodyTextarea.value;
  if (!textarea) return;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const text = form.body;
  const selected = text.substring(start, end);

  if (selected) {
    form.body =
      text.substring(0, start) +
      before +
      selected +
      after +
      text.substring(end);
    nextTick(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, end + before.length);
    });
  } else {
    form.body = text.substring(0, start) + before + after + text.substring(end);
    nextTick(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length);
    });
  }
};

const insertAtCursor = text => {
  const textarea = bodyTextarea.value;
  if (!textarea) {
    form.body += text;
    return;
  }
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  form.body = form.body.substring(0, start) + text + form.body.substring(end);
  nextTick(() => {
    textarea.focus();
    textarea.setSelectionRange(start + text.length, start + text.length);
  });
};

const insertVariable = () => {
  const next =
    (detectedVariables.value[detectedVariables.value.length - 1] || 0) + 1;
  insertAtCursor(`{{${next}}}`);
};

const formatBold = () => wrapSelection('*', '*');
const formatItalic = () => wrapSelection('_', '_');
const formatStrike = () => wrapSelection('~', '~');
const formatCode = () => wrapSelection('```', '```');
const insertNewline = () => insertAtCursor('\n');
const insertEmoji = emoji => {
  insertAtCursor(emoji);
  showEmojiPicker.value = false;
};

// ── Preview (renderiza formatação do WhatsApp) ───────────────────────────
const escapeHtml = s =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const renderWhatsappText = (text, varValues = []) => {
  let html = escapeHtml(text);
  html = html.replace(/```([^`]+)```/g, '<code>$1</code>');
  html = html.replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>');
  html = html.replace(/_([^_\n]+)_/g, '<em>$1</em>');
  html = html.replace(/~([^~\n]+)~/g, '<s>$1</s>');
  html = html.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, n) => {
    const val = varValues[parseInt(n, 10) - 1];
    if (val) return `<strong>${escapeHtml(val)}</strong>`;
    return `<span class="px-1 rounded bg-n-amber-3 text-n-amber-11">{{${n}}}</span>`;
  });
  return html.replace(/\n/g, '<br>');
};

const previewHeaderHtml = computed(() =>
  renderWhatsappText(form.header, form.variableExamples)
);
const previewBodyHtml = computed(() =>
  renderWhatsappText(form.body, form.variableExamples)
);

const buttonIcon = type => {
  if (type === 'URL') return 'i-lucide-external-link';
  if (type === 'PHONE_NUMBER') return 'i-lucide-phone';
  return 'i-lucide-reply';
};

// ── Listagem ─────────────────────────────────────────────────────────────
const getBodyText = tmpl =>
  tmpl.components?.find(c => c.type === 'BODY')?.text || '';
const getHeaderFormat = tmpl =>
  tmpl.components?.find(c => c.type === 'HEADER')?.format || null;

const HEADER_ICONS = {
  IMAGE: 'i-lucide-image',
  VIDEO: 'i-lucide-video',
  DOCUMENT: 'i-lucide-file-text',
};

const statusConfig = {
  APPROVED: {
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.APPROVED'),
    classes: 'bg-n-teal-2 text-n-teal-11',
  },
  PENDING: {
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.PENDING'),
    classes: 'bg-n-amber-2 text-n-amber-11',
  },
  REJECTED: {
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.REJECTED'),
    classes: 'bg-n-ruby-2 text-n-ruby-11',
  },
};

const getStatus = status =>
  statusConfig[status] || {
    label: status,
    classes: 'bg-n-slate-2 text-n-slate-11',
  };

const sortedTemplates = computed(() => {
  const list = Array.isArray(templates.value) ? templates.value : [];
  return [...list].sort((a, b) => {
    const order = { APPROVED: 0, PENDING: 1, REJECTED: 2 };
    return (order[a.status] ?? 3) - (order[b.status] ?? 3);
  });
});

const estimatedCost = computed(() => {
  if (!usageData.value?.per_template) return 0;
  let total = 0;
  usageData.value.per_template.forEach(item => {
    const tmpl = templates.value.find(tp => tp.name === item.name);
    const category = tmpl?.category || 'MARKETING';
    total +=
      item.count * (TEMPLATE_COSTS[category] || TEMPLATE_COSTS.MARKETING);
  });
  return total;
});

const businessAccountId = computed(
  () => props.inbox.provider_config?.business_account_id || ''
);
const metaBusinessUrl = computed(() => {
  const baid = businessAccountId.value;
  return baid
    ? `https://business.facebook.com/wa/manage/message-templates/?waba_id=${baid}`
    : 'https://business.facebook.com/settings/whatsapp-business-accounts';
});
const metaPaymentUrl = computed(
  () => 'https://business.facebook.com/billing_hub/payment_methods'
);

// ── API ──────────────────────────────────────────────────────────────────
const fetchTemplates = async () => {
  isLoading.value = true;
  try {
    const { data } = await InboxesAPI.getTemplates(props.inbox.id);
    templates.value = data.templates || [];
  } catch {
    templates.value = [];
  } finally {
    isLoading.value = false;
  }
};

const fetchUsage = async () => {
  isLoadingUsage.value = true;
  try {
    const { data } = await InboxesAPI.getTemplateUsage(
      props.inbox.id,
      usagePeriod.value
    );
    usageData.value = data;
  } catch {
    usageData.value = null;
  } finally {
    isLoadingUsage.value = false;
  }
};

const syncTemplates = async () => {
  isSyncing.value = true;
  try {
    const { data } = await InboxesAPI.syncTemplatesManual(props.inbox.id);
    templates.value = data.templates || [];
    toast.success(t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALERTS.SYNCED'));
  } catch {
    toast.error(t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALERTS.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

const onMediaSelect = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  if (!['image/jpeg', 'image/png'].includes(file.type)) {
    toast.error(
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_IMAGE_ERROR_TYPE')
    );
    return;
  }
  isUploadingMedia.value = true;
  try {
    const { data } = await InboxesAPI.uploadTemplateMedia(props.inbox.id, file);
    form.headerHandle = data.handle;
    form.headerImagePreview = URL.createObjectURL(file);
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_IMAGE_ERROR');
    toast.error(msg);
  } finally {
    isUploadingMedia.value = false;
  }
};

const setTemplateType = value => {
  form.templateType = value;
  // LTO exige categoria MARKETING na Meta.
  if (value === 'LTO') form.category = 'MARKETING';
  // Autenticação tem categoria própria.
  if (value === 'AUTH') form.category = 'AUTHENTICATION';
  else if (form.category === 'AUTHENTICATION') form.category = 'MARKETING';
};

// ── Carrossel ─────────────────────────────────────────────────────────────
const addCard = () => {
  if (form.cards.length >= CAROUSEL_LIMITS.cards) return;
  form.cards.push({ headerHandle: '', headerImagePreview: '', body: '' });
};
const removeCard = index => form.cards.splice(index, 1);

const onCardMediaSelect = async (index, event) => {
  const file = event.target.files?.[0];
  if (!file) return;
  if (!['image/jpeg', 'image/png'].includes(file.type)) {
    toast.error(
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_IMAGE_ERROR_TYPE')
    );
    return;
  }
  isUploadingMedia.value = true;
  try {
    const { data } = await InboxesAPI.uploadTemplateMedia(props.inbox.id, file);
    form.cards[index].headerHandle = data.handle;
    form.cards[index].headerImagePreview = URL.createObjectURL(file);
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_IMAGE_ERROR');
    toast.error(msg);
  } finally {
    isUploadingMedia.value = false;
  }
};

const addCarouselButton = () => {
  if (form.carouselButtons.length >= CAROUSEL_LIMITS.buttons) return;
  form.carouselButtons.push({
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    phone: '',
  });
};
const removeCarouselButton = index => form.carouselButtons.splice(index, 1);

// ── Validação ──────────────────────────────────────────────────────────
const validateForm = () => {
  formErrors.name = '';
  formErrors.body = '';
  formErrors.general = '';
  let valid = true;

  if (!form.name.trim()) {
    formErrors.name = t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_ERROR');
    valid = false;
  } else if (!/^[a-z][a-z0-9_]*$/.test(form.name)) {
    formErrors.name = t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_FORMAT_ERROR');
    valid = false;
  }

  // Autenticação: a Meta gera o corpo; só o nome precisa ser válido.
  if (isAuth.value) return valid;

  if (!form.body.trim()) {
    formErrors.body = t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_ERROR');
    valid = false;
  }

  if (
    detectedVariables.value.some((_, i) => !form.variableExamples[i]?.trim())
  ) {
    formErrors.general = t(
      'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.VAR_EXAMPLES_REQUIRED'
    );
    valid = false;
  }

  if (isCarousel.value) {
    if (form.cards.length < CAROUSEL_LIMITS.minCards) {
      formErrors.general = t(
        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CAROUSEL_MIN_CARDS'
      );
      valid = false;
    } else if (form.cards.some(c => !c.headerHandle)) {
      formErrors.general = t(
        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CAROUSEL_CARD_IMAGE_REQUIRED'
      );
      valid = false;
    } else if (form.cards.some(c => !c.body.trim())) {
      formErrors.general = t(
        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CAROUSEL_CARD_BODY_REQUIRED'
      );
      valid = false;
    } else if (
      form.carouselButtons.some(
        b =>
          !b.text.trim() ||
          (b.type === 'URL' && !b.url.trim()) ||
          (b.type === 'PHONE_NUMBER' && !b.phone.trim())
      )
    ) {
      formErrors.general = t(
        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CAROUSEL_BUTTON_INCOMPLETE'
      );
      valid = false;
    }
    return valid;
  }

  if (isLto.value) {
    if (!form.lto.text.trim()) {
      formErrors.general = t(
        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LTO_TEXT_ERROR'
      );
      valid = false;
    } else if (!form.lto.code.trim()) {
      formErrors.general = t(
        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LTO_CODE_ERROR'
      );
      valid = false;
    } else if (!form.lto.urlText.trim() || !form.lto.url.trim()) {
      formErrors.general = t(
        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LTO_URL_ERROR'
      );
      valid = false;
    }
    return valid;
  }

  if (
    ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerFormat) &&
    !form.headerHandle
  ) {
    formErrors.general = t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.MEDIA_REQUIRED');
    valid = false;
  }

  form.buttons.forEach(b => {
    if (!b.text.trim()) {
      formErrors.general = t(
        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_TEXT_REQUIRED'
      );
      valid = false;
    }
    if (b.type === 'URL' && !b.url.trim()) {
      formErrors.general = t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.URL_REQUIRED');
      valid = false;
    }
    if (b.type === 'PHONE_NUMBER' && !b.phone.trim()) {
      formErrors.general = t(
        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.PHONE_REQUIRED'
      );
      valid = false;
    }
  });

  return valid;
};

const resetForm = () => {
  form.name = '';
  form.category = 'MARKETING';
  form.language = 'pt_BR';
  form.templateType = 'STANDARD';
  form.headerFormat = 'NONE';
  form.header = '';
  form.headerHandle = '';
  form.headerImagePreview = '';
  form.headerExample = '';
  form.body = '';
  form.footer = '';
  form.buttons = [];
  form.variableExamples = [];
  form.cards = [];
  form.carouselButtons = [];
  form.lto = {
    text: 'Oferta por tempo limitado',
    hasExpiration: true,
    code: '',
    urlText: '',
    url: '',
  };
  form.auth = { codeExpirationMinutes: 5, addSecurityRecommendation: true };
  editingId.value = null;
  formErrors.name = '';
  formErrors.body = '';
  formErrors.general = '';
};

const toMetaButton = b => {
  if (b.type === 'URL') {
    const urlButton = { type: 'URL', text: b.text, url: b.url };
    // URL com variável ({{1}}) exige um exemplo de URL completa.
    if (b.url.includes('{{')) {
      urlButton.example = [
        b.urlExample || b.url.replace(/\{\{\d+\}\}/g, '123'),
      ];
    }
    return urlButton;
  }
  if (b.type === 'PHONE_NUMBER') {
    return { type: 'PHONE_NUMBER', text: b.text, phone_number: b.phone };
  }
  return { type: 'QUICK_REPLY', text: b.text };
};

const buildCarouselComponents = () => {
  const bodyComponent = { type: 'BODY', text: form.body };
  if (detectedVariables.value.length) {
    bodyComponent.example = {
      body_text: [
        detectedVariables.value.map(
          (_, i) => form.variableExamples[i] || `exemplo${i + 1}`
        ),
      ],
    };
  }

  const sharedButtons = form.carouselButtons.length
    ? [{ type: 'BUTTONS', buttons: form.carouselButtons.map(toMetaButton) }]
    : [];

  const cards = form.cards.map(card => ({
    components: [
      {
        type: 'HEADER',
        format: 'IMAGE',
        example: { header_handle: [card.headerHandle] },
      },
      { type: 'BODY', text: card.body },
      ...sharedButtons,
    ],
  }));

  return [bodyComponent, { type: 'CAROUSEL', cards }];
};

const buildLtoComponents = () => {
  const components = [
    {
      type: 'LIMITED_TIME_OFFER',
      limited_time_offer: {
        text: form.lto.text,
        has_expiration: form.lto.hasExpiration,
      },
    },
  ];

  const bodyComponent = { type: 'BODY', text: form.body };
  if (detectedVariables.value.length) {
    bodyComponent.example = {
      body_text: [
        detectedVariables.value.map(
          (_, i) => form.variableExamples[i] || `exemplo${i + 1}`
        ),
      ],
    };
  }
  components.push(bodyComponent);

  // LTO exige um botão de cupom (COPY_CODE) seguido de um botão de URL.
  components.push({
    type: 'BUTTONS',
    buttons: [
      { type: 'COPY_CODE', example: form.lto.code },
      { type: 'URL', text: form.lto.urlText, url: form.lto.url },
    ],
  });

  return components;
};

const buildAuthComponents = () => {
  const components = [
    {
      type: 'BODY',
      add_security_recommendation: form.auth.addSecurityRecommendation,
    },
  ];
  if (form.auth.codeExpirationMinutes) {
    components.push({
      type: 'FOOTER',
      code_expiration_minutes: Number(form.auth.codeExpirationMinutes),
    });
  }
  components.push({
    type: 'BUTTONS',
    buttons: [{ type: 'OTP', otp_type: 'COPY_CODE' }],
  });
  return components;
};

const buildComponents = () => {
  if (isCarousel.value) return buildCarouselComponents();
  if (isLto.value) return buildLtoComponents();
  if (isAuth.value) return buildAuthComponents();

  const components = [];

  if (form.headerFormat === 'TEXT' && form.header.trim()) {
    const headerComponent = {
      type: 'HEADER',
      format: 'TEXT',
      text: form.header,
    };
    if (headerHasVariable.value) {
      headerComponent.example = {
        header_text: [form.headerExample || 'exemplo'],
      };
    }
    components.push(headerComponent);
  } else if (
    ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerFormat) &&
    form.headerHandle
  ) {
    components.push({
      type: 'HEADER',
      format: form.headerFormat,
      example: { header_handle: [form.headerHandle] },
    });
  }

  const bodyComponent = { type: 'BODY', text: form.body };
  if (detectedVariables.value.length) {
    bodyComponent.example = {
      body_text: [
        detectedVariables.value.map(
          (_, i) => form.variableExamples[i] || `exemplo${i + 1}`
        ),
      ],
    };
  }
  components.push(bodyComponent);

  if (form.footer.trim()) {
    components.push({ type: 'FOOTER', text: form.footer });
  }

  if (form.buttons.length) {
    components.push({
      type: 'BUTTONS',
      buttons: form.buttons.map(toMetaButton),
    });
  }

  return components;
};

const handleCreate = async () => {
  if (!validateForm()) return;

  isCreating.value = true;
  try {
    const payload = {
      name: form.name,
      category: form.category,
      language: form.language,
      components: buildComponents(),
    };
    if (editingId.value) {
      await InboxesAPI.editTemplate(props.inbox.id, editingId.value, payload);
    } else {
      await InboxesAPI.createTemplate(props.inbox.id, payload);
    }
    toast.success(t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALERTS.CREATED'));
    showCreateModal.value = false;
    resetForm();
    await fetchTemplates();
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALERTS.CREATE_ERROR');
    toast.error(msg);
  } finally {
    isCreating.value = false;
  }
};

const handleDelete = async templateName => {
  try {
    await InboxesAPI.deleteTemplate(props.inbox.id, templateName);
    toast.success(t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALERTS.DELETED'));
    deleteConfirmName.value = null;
    await fetchTemplates();
  } catch {
    toast.error(t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALERTS.DELETE_ERROR'));
  }
};

const addButton = () => {
  if (form.buttons.length >= 3) return;
  form.buttons.push({
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    phone: '',
    urlExample: '',
  });
};
const removeButton = index => form.buttons.splice(index, 1);

const openCreateModal = () => {
  resetForm();
  showCreateModal.value = true;
};

const applyStarter = starter => {
  resetForm();
  form.name = `${starter.name}_${Date.now().toString().slice(-4)}`;
  form.category = starter.category;
  form.headerFormat = starter.headerFormat;
  form.header = starter.header;
  form.body = starter.body;
  form.footer = starter.footer;
  form.buttons = starter.buttons.map(b => ({ ...b }));
  form.variableExamples = [...starter.variableExamples];
  showCreateModal.value = true;
};

const prefillFromTemplate = tmpl => {
  resetForm();
  form.category = tmpl.category || 'MARKETING';
  form.language = tmpl.language || 'pt_BR';

  const carousel = (tmpl.components || []).find(c => c.type === 'CAROUSEL');
  if (carousel) {
    form.templateType = 'CAROUSEL';
    const body = (tmpl.components || []).find(c => c.type === 'BODY');
    form.body = body?.text || '';
    form.variableExamples = [...(body?.example?.body_text?.[0] || [])];
    // A Meta não devolve as imagens dos cards; o usuário reenvia ao editar.
    form.cards = (carousel.cards || []).map(card => {
      const cardBody = (card.components || []).find(cc => cc.type === 'BODY');
      return {
        headerHandle: '',
        headerImagePreview: '',
        body: cardBody?.text || '',
      };
    });
    const firstButtons = (carousel.cards?.[0]?.components || []).find(
      cc => cc.type === 'BUTTONS'
    );
    form.carouselButtons = (firstButtons?.buttons || []).map(b => ({
      type:
        b.type === 'URL' || b.type === 'PHONE_NUMBER' ? b.type : 'QUICK_REPLY',
      text: b.text || '',
      url: b.url || '',
      phone: b.phone_number || '',
    }));
    return;
  }

  if (tmpl.category === 'AUTHENTICATION') {
    form.templateType = 'AUTH';
    form.category = 'AUTHENTICATION';
    const body = (tmpl.components || []).find(c => c.type === 'BODY');
    const footer = (tmpl.components || []).find(c => c.type === 'FOOTER');
    form.auth = {
      codeExpirationMinutes: footer?.code_expiration_minutes || 5,
      addSecurityRecommendation: body?.add_security_recommendation !== false,
    };
    return;
  }

  const offer = (tmpl.components || []).find(
    c => c.type === 'LIMITED_TIME_OFFER'
  );
  if (offer) {
    form.templateType = 'LTO';
    const body = (tmpl.components || []).find(c => c.type === 'BODY');
    form.body = body?.text || '';
    form.variableExamples = [...(body?.example?.body_text?.[0] || [])];
    const ltoData = offer.limited_time_offer || {};
    const btns =
      (tmpl.components || []).find(c => c.type === 'BUTTONS')?.buttons || [];
    const copyBtn = btns.find(b => b.type === 'COPY_CODE');
    const urlBtn = btns.find(b => b.type === 'URL');
    form.lto = {
      text: ltoData.text || 'Oferta por tempo limitado',
      hasExpiration: ltoData.has_expiration !== false,
      code: Array.isArray(copyBtn?.example)
        ? copyBtn.example[0] || ''
        : copyBtn?.example || '',
      urlText: urlBtn?.text || '',
      url: urlBtn?.url || '',
    };
    return;
  }

  (tmpl.components || []).forEach(c => {
    if (c.type === 'HEADER') {
      if (c.format === 'TEXT') {
        form.headerFormat = 'TEXT';
        form.header = c.text || '';
        form.headerExample = c.example?.header_text?.[0] || '';
      } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(c.format)) {
        // a Meta não devolve a mídia original; usuário reenvia
        form.headerFormat = 'IMAGE';
      }
    } else if (c.type === 'BODY') {
      form.body = c.text || '';
      form.variableExamples = [...(c.example?.body_text?.[0] || [])];
    } else if (c.type === 'FOOTER') {
      form.footer = c.text || '';
    } else if (c.type === 'BUTTONS') {
      form.buttons = (c.buttons || []).slice(0, 3).map(b => ({
        type:
          b.type === 'URL' || b.type === 'PHONE_NUMBER'
            ? b.type
            : 'QUICK_REPLY',
        text: b.text || '',
        url: b.url || '',
        phone: b.phone_number || '',
        urlExample: b.example?.[0] || '',
      }));
    }
  });
};

const duplicateTemplate = tmpl => {
  prefillFromTemplate(tmpl);
  form.name = `${tmpl.name}_copia`.slice(0, 60);
  editingId.value = null;
  showCreateModal.value = true;
};

const openEditModal = tmpl => {
  prefillFromTemplate(tmpl);
  form.name = tmpl.name;
  editingId.value = tmpl.id;
  showCreateModal.value = true;
};

const isEditable = tmpl =>
  ['APPROVED', 'REJECTED', 'PAUSED'].includes(tmpl.status);

const openViewModal = tmpl => {
  viewTemplate.value = tmpl;
};

const viewData = computed(() => {
  const tmpl = viewTemplate.value;
  if (!tmpl) return null;
  const comps = tmpl.components || [];
  const header = comps.find(c => c.type === 'HEADER');
  const body = comps.find(c => c.type === 'BODY');
  const footer = comps.find(c => c.type === 'FOOTER');
  const buttonsComp = comps.find(c => c.type === 'BUTTONS');
  const offer = comps.find(c => c.type === 'LIMITED_TIME_OFFER');
  const ltoButtonText = b => {
    if (b.type !== 'COPY_CODE') return b.text || '';
    return Array.isArray(b.example) ? b.example[0] || '' : b.example || '';
  };
  const ltoButtons = offer
    ? (buttonsComp?.buttons || []).map(b => ({
        type: b.type,
        text: ltoButtonText(b),
      }))
    : [];
  const carousel = comps.find(c => c.type === 'CAROUSEL');
  const cards = carousel
    ? (carousel.cards || []).map(card => {
        const cc = card.components || [];
        const cardButtons = cc.find(x => x.type === 'BUTTONS');
        return {
          body: cc.find(x => x.type === 'BODY')?.text || '',
          buttons: (cardButtons?.buttons || []).map(b => ({
            type: b.type,
            text: b.text || '',
          })),
        };
      })
    : [];
  const isAuthView = tmpl.category === 'AUTHENTICATION';
  return {
    name: tmpl.name,
    isCarousel: !!carousel,
    isLto: !!offer,
    isAuth: isAuthView,
    authExpiration: comps.find(c => c.type === 'FOOTER')
      ?.code_expiration_minutes,
    ltoText: offer?.limited_time_offer?.text || '',
    ltoButtons,
    cards,
    headerFormat: header?.format || 'NONE',
    headerText: header?.format === 'TEXT' ? header.text || '' : '',
    body: body?.text || '',
    footer: footer?.text || '',
    buttons: (buttonsComp?.buttons || []).map(b => ({
      type: b.type,
      text: b.text || '',
    })),
    varExamples: body?.example?.body_text?.[0] || [],
  };
});

const formatCurrency = value => `$${value.toFixed(2)}`;
const costPerMessage = category => {
  const cost = TEMPLATE_COSTS[category] || TEMPLATE_COSTS.MARKETING;
  return `~${formatCurrency(cost)}/${t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.PER_MSG')}`;
};

onMounted(() => {
  fetchTemplates();
  fetchUsage();
});
</script>

<template>
  <div class="mx-8 flex flex-col gap-6">
    <!-- Meta Quick Links -->
    <div
      class="flex flex-wrap items-center gap-3 p-4 rounded-xl border border-n-blue-6 bg-n-blue-2"
    >
      <span class="i-lucide-external-link size-4 text-n-blue-11 shrink-0" />
      <span class="text-sm text-n-blue-11 font-medium">
        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.META_LINKS.LABEL') }}
      </span>
      <div class="flex items-center gap-2 ml-auto">
        <a
          :href="metaBusinessUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4 transition-colors"
        >
          <span class="i-lucide-layout-template size-3.5" />
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.META_LINKS.MANAGE_TEMPLATES') }}
        </a>
        <a
          :href="metaPaymentUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4 transition-colors"
        >
          <span class="i-lucide-credit-card size-3.5" />
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.META_LINKS.PAYMENT_METHODS') }}
        </a>
      </div>
    </div>

    <!-- Templates -->
    <SettingsSectionCard
      :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.TITLE')"
      :description="t('INBOX_MGMT.WHATSAPP_TEMPLATES.DESCRIPTION')"
    >
      <template #header-actions>
        <div class="flex items-center gap-2">
          <Button
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC')"
            icon="i-lucide-refresh-cw"
            slate
            faded
            xs
            :is-loading="isSyncing"
            @click="syncTemplates"
          />
          <Button
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.NEW')"
            icon="i-lucide-plus"
            blue
            xs
            @click="openCreateModal"
          />
        </div>
      </template>

      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-10" />
      </div>

      <div
        v-else-if="!sortedTemplates.length"
        class="flex flex-col items-center justify-center gap-4 py-12"
      >
        <span class="i-lucide-message-square-plus size-10 text-n-slate-8" />
        <div class="flex flex-col items-center gap-1.5 max-w-sm text-center">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY_STATE.TITLE') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY_STATE.DESCRIPTION') }}
          </span>
        </div>
        <Button
          :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.NEW')"
          icon="i-lucide-plus"
          blue
          xs
          @click="openCreateModal"
        />
      </div>

      <div v-else class="flex flex-col gap-2">
        <div
          v-for="tmpl in sortedTemplates"
          :key="tmpl.name + tmpl.language"
          class="flex items-start justify-between gap-4 p-4 border rounded-xl bg-n-solid-1 border-n-weak"
        >
          <div class="flex flex-col gap-1.5 min-w-0 flex-1">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-sm font-semibold text-n-slate-12 font-mono">
                {{ tmpl.name }}
              </span>
              <span
                class="px-1.5 py-0.5 text-[11px] font-medium rounded"
                :class="getStatus(tmpl.status).classes"
              >
                {{ getStatus(tmpl.status).label }}
              </span>
            </div>
            <div class="flex items-center gap-2 text-xs text-n-slate-10">
              <span>{{ tmpl.category }}</span>
              <span>·</span>
              <span>{{ tmpl.language }}</span>
              <template v-if="HEADER_ICONS[getHeaderFormat(tmpl)]">
                <span>·</span>
                <span class="inline-flex items-center gap-1">
                  <span
                    :class="HEADER_ICONS[getHeaderFormat(tmpl)]"
                    class="size-3"
                  />
                  {{ getHeaderFormat(tmpl) }}
                </span>
              </template>
              <span>·</span>
              <span>{{ costPerMessage(tmpl.category) }}</span>
            </div>
            <p
              v-if="getBodyText(tmpl)"
              class="text-sm text-n-slate-11 line-clamp-2 mt-0.5"
            >
              {{ getBodyText(tmpl) }}
            </p>
          </div>

          <div class="flex items-center gap-1 shrink-0">
            <button
              class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-solid-3 cursor-pointer transition-colors"
              :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.VIEW')"
              @click="openViewModal(tmpl)"
            >
              <span class="i-lucide-eye size-4" />
            </button>
            <button
              v-if="isEditable(tmpl)"
              class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-amber-11 hover:bg-n-amber-2 cursor-pointer transition-colors"
              :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.EDIT')"
              @click="openEditModal(tmpl)"
            >
              <span class="i-lucide-pencil size-4" />
            </button>
            <button
              class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-blue-11 hover:bg-n-blue-2 cursor-pointer transition-colors"
              :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.DUPLICATE')"
              @click="duplicateTemplate(tmpl)"
            >
              <span class="i-lucide-copy size-4" />
            </button>
            <button
              v-if="deleteConfirmName === tmpl.name"
              class="px-2 py-1 text-xs font-medium rounded text-n-ruby-11 bg-n-ruby-2 hover:bg-n-ruby-3 cursor-pointer transition-colors"
              @click="handleDelete(tmpl.name)"
            >
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.CONFIRM_DELETE') }}
            </button>
            <button
              class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-ruby-2 cursor-pointer transition-colors"
              :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE')"
              @click="
                deleteConfirmName =
                  deleteConfirmName === tmpl.name ? null : tmpl.name
              "
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>
    </SettingsSectionCard>

    <!-- Galeria de modelos -->
    <SettingsSectionCard
      :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.GALLERY.TITLE')"
      :description="t('INBOX_MGMT.WHATSAPP_TEMPLATES.GALLERY.DESCRIPTION')"
    >
      <div class="grid sm:grid-cols-3 gap-3">
        <button
          v-for="starter in STARTER_TEMPLATES"
          :key="starter.key"
          class="flex flex-col gap-1.5 p-4 text-left border rounded-xl bg-n-solid-1 border-n-weak hover:border-n-brand hover:shadow-sm cursor-pointer transition-all"
          @click="applyStarter(starter)"
        >
          <span class="text-sm font-semibold text-n-slate-12">
            {{
              t(
                `INBOX_MGMT.WHATSAPP_TEMPLATES.GALLERY.${starter.key.toUpperCase()}`
              )
            }}
          </span>
          <span class="text-xs text-n-slate-10 line-clamp-2">{{
            starter.body
          }}</span>
          <span
            class="mt-1 inline-flex items-center gap-1 text-xs font-medium text-n-brand"
          >
            <span class="i-lucide-wand-2 size-3" />
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.GALLERY.USE') }}
          </span>
        </button>
      </div>
    </SettingsSectionCard>

    <!-- Pricing -->
    <SettingsSectionCard
      v-if="sortedTemplates.length"
      :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.TITLE')"
      :description="t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.DESCRIPTION')"
    >
      <div class="overflow-hidden rounded-lg border border-n-weak">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-n-solid-3">
              <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.CATEGORY') }}
              </th>
              <th class="px-4 py-2.5 text-right font-medium text-n-slate-11">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.PRICE_USD') }}
              </th>
              <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.NOTES') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-t border-n-weak">
              <td class="px-4 py-2.5 text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.MARKETING_LABEL') }}
              </td>
              <td class="px-4 py-2.5 text-right font-mono text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.MARKETING_PRICE') }}
              </td>
              <td class="px-4 py-2.5 text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.MARKETING_NOTE') }}
              </td>
            </tr>
            <tr class="border-t border-n-weak">
              <td class="px-4 py-2.5 text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.UTILITY_LABEL') }}
              </td>
              <td class="px-4 py-2.5 text-right font-mono text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.UTILITY_PRICE') }}
              </td>
              <td class="px-4 py-2.5 text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.UTILITY_NOTE') }}
              </td>
            </tr>
            <tr class="border-t border-n-weak">
              <td class="px-4 py-2.5 text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.AUTH_LABEL') }}
              </td>
              <td class="px-4 py-2.5 text-right font-mono text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.AUTH_PRICE') }}
              </td>
              <td class="px-4 py-2.5 text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.AUTH_NOTE') }}
              </td>
            </tr>
            <tr class="border-t border-n-weak">
              <td class="px-4 py-2.5 text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.SERVICE_LABEL') }}
              </td>
              <td class="px-4 py-2.5 text-right font-mono text-n-teal-11">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.FREE') }}
              </td>
              <td class="px-4 py-2.5 text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.SERVICE_NOTE') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="mt-2 text-xs text-n-slate-9">
        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.DISCLAIMER') }}
      </p>
    </SettingsSectionCard>

    <!-- Usage -->
    <SettingsSectionCard
      v-if="sortedTemplates.length"
      :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.TITLE')"
      :description="t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.DESCRIPTION')"
    >
      <template #header-actions>
        <select
          v-model="usagePeriod"
          class="px-2 py-1 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 cursor-pointer"
          @change="fetchUsage"
        >
          <option v-for="days in PERIOD_OPTIONS" :key="days" :value="days">
            {{ days }} {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.DAYS') }}
          </option>
        </select>
      </template>

      <div v-if="isLoadingUsage" class="flex items-center justify-center py-8">
        <span class="i-lucide-loader-2 size-4 animate-spin text-n-slate-10" />
      </div>

      <div v-else-if="usageData" class="flex flex-col gap-4">
        <div class="grid grid-cols-2 gap-3">
          <div
            class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
          >
            <span class="text-xs text-n-slate-10">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.TOTAL_SENT') }}
            </span>
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ usageData.total_sent }}
            </span>
          </div>
          <div
            class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
          >
            <span class="text-xs text-n-slate-10">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.ESTIMATED_COST') }}
            </span>
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ formatCurrency(estimatedCost) }}
            </span>
          </div>
        </div>

        <div
          v-if="usageData.per_template?.length"
          class="overflow-hidden rounded-lg border border-n-weak"
        >
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-n-solid-3">
                <th class="px-4 py-2 text-left font-medium text-n-slate-11">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.TEMPLATE') }}
                </th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.SENDS') }}
                </th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.COST') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in usageData.per_template"
                :key="item.name"
                class="border-t border-n-weak"
              >
                <td class="px-4 py-2 font-mono text-n-slate-12">
                  {{ item.name }}
                </td>
                <td class="px-4 py-2 text-right text-n-slate-12">
                  {{ item.count }}
                </td>
                <td class="px-4 py-2 text-right font-mono text-n-slate-12">
                  {{
                    formatCurrency(
                      item.count *
                        (TEMPLATE_COSTS[
                          templates.find(tp => tp.name === item.name)?.category
                        ] || 0.0625)
                    )
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="py-6 text-center text-sm text-n-slate-10">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.NO_SENDS') }}
        </div>
      </div>
    </SettingsSectionCard>

    <!-- Create Modal -->
    <teleport to="body">
      <div
        v-if="showCreateModal"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
        @click.self="showCreateModal = false"
      >
        <div
          class="w-full max-w-4xl max-h-[92vh] flex flex-col rounded-2xl bg-n-solid-2 border border-n-weak shadow-2xl overflow-hidden"
        >
          <div
            class="flex items-center justify-between p-5 border-b border-n-weak"
          >
            <h3 class="text-base font-semibold text-n-slate-12">
              {{
                editingId
                  ? t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.TITLE_EDIT')
                  : t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.TITLE')
              }}
            </h3>
            <button
              class="p-1 rounded-lg text-n-slate-10 hover:bg-n-solid-3 cursor-pointer transition-colors"
              @click="showCreateModal = false"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <div class="grid md:grid-cols-2 overflow-hidden flex-1 min-h-0">
            <!-- FORM column -->
            <div class="flex flex-col gap-4 p-5 overflow-y-auto">
              <!-- Name -->
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_LABEL') }}
                </label>
                <input
                  v-model="form.name"
                  type="text"
                  :disabled="!!editingId"
                  class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand font-mono disabled:opacity-60 disabled:cursor-not-allowed"
                  :placeholder="
                    t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_PLACEHOLDER')
                  "
                  @input="
                    form.name = form.name
                      .toLowerCase()
                      .replace(/[^a-z0-9_]/g, '')
                  "
                />
                <span v-if="editingId" class="text-xs text-n-amber-11">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EDIT_LOCKED_HINT') }}
                </span>
                <span class="text-xs text-n-slate-9">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_HINT') }}
                </span>
                <span v-if="formErrors.name" class="text-xs text-n-ruby-11">
                  {{ formErrors.name }}
                </span>
              </div>

              <!-- Category -->
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY_LABEL') }}
                </label>
                <div class="flex items-center gap-4">
                  <label
                    class="flex items-center gap-2 cursor-pointer"
                    @click="form.category = 'MARKETING'"
                  >
                    <span
                      class="size-4 rounded-full border-2 grid place-content-center transition-colors"
                      :class="
                        form.category === 'MARKETING'
                          ? 'border-n-brand bg-n-brand'
                          : 'border-n-slate-8'
                      "
                    >
                      <span
                        v-if="form.category === 'MARKETING'"
                        class="size-1.5 rounded-full bg-white"
                      />
                    </span>
                    <span class="text-sm text-n-slate-12">
                      {{
                        t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.MARKETING_LABEL')
                      }}
                    </span>
                  </label>
                  <label
                    class="flex items-center gap-2 cursor-pointer"
                    @click="form.category = 'UTILITY'"
                  >
                    <span
                      class="size-4 rounded-full border-2 grid place-content-center transition-colors"
                      :class="
                        form.category === 'UTILITY'
                          ? 'border-n-brand bg-n-brand'
                          : 'border-n-slate-8'
                      "
                    >
                      <span
                        v-if="form.category === 'UTILITY'"
                        class="size-1.5 rounded-full bg-white"
                      />
                    </span>
                    <span class="text-sm text-n-slate-12">
                      {{
                        t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.UTILITY_LABEL')
                      }}
                    </span>
                  </label>
                </div>
              </div>

              <!-- Language -->
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE_LABEL') }}
                </label>
                <select
                  v-model="form.language"
                  :disabled="!!editingId"
                  class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <option
                    v-for="lang in LANGUAGES"
                    :key="lang.code"
                    :value="lang.code"
                  >
                    {{ lang.label }}
                  </option>
                </select>
              </div>

              <!-- Template type -->
              <div v-if="!editingId" class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.TYPE_LABEL') }}
                </label>
                <div class="grid grid-cols-2 gap-2">
                  <button
                    v-for="tt in TEMPLATE_TYPES"
                    :key="tt.value"
                    type="button"
                    class="flex items-center gap-2 p-3 text-left border rounded-lg transition-colors cursor-pointer"
                    :class="
                      form.templateType === tt.value
                        ? 'border-n-brand bg-n-brand-2'
                        : 'border-n-weak bg-n-solid-1 hover:border-n-slate-7'
                    "
                    @click="setTemplateType(tt.value)"
                  >
                    <span
                      class="size-4 shrink-0"
                      :class="[
                        tt.icon,
                        form.templateType === tt.value
                          ? 'text-n-brand-11'
                          : 'text-n-slate-10',
                      ]"
                    />
                    <span
                      class="text-sm font-medium"
                      :class="
                        form.templateType === tt.value
                          ? 'text-n-brand-11'
                          : 'text-n-slate-12'
                      "
                    >
                      {{ t(tt.label) }}
                    </span>
                  </button>
                </div>
              </div>

              <!-- Header -->
              <div v-if="isStandard" class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_LABEL') }}
                </label>
                <select
                  v-model="form.headerFormat"
                  class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand cursor-pointer"
                >
                  <option
                    v-for="fmt in HEADER_FORMATS"
                    :key="fmt.value"
                    :value="fmt.value"
                  >
                    {{ t(fmt.label) }}
                  </option>
                </select>

                <div
                  v-if="form.headerFormat === 'TEXT'"
                  class="flex flex-col gap-1"
                >
                  <input
                    v-model="form.header"
                    type="text"
                    :maxlength="LIMITS.header"
                    class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
                    :placeholder="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_PLACEHOLDER')
                    "
                  />
                  <span class="text-xs text-n-slate-9 self-end">
                    {{ headerCharCount }}/{{ LIMITS.header }}
                  </span>
                  <input
                    v-if="headerHasVariable"
                    v-model="form.headerExample"
                    type="text"
                    class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
                    :placeholder="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_VAR_EXAMPLE')
                    "
                  />
                </div>

                <div
                  v-else-if="form.headerFormat === 'IMAGE'"
                  class="flex flex-col gap-2"
                >
                  <div
                    v-if="form.headerImagePreview"
                    class="flex items-center gap-3 p-2 rounded-lg border border-n-weak bg-n-solid-1"
                  >
                    <img
                      :src="form.headerImagePreview"
                      class="size-12 rounded object-cover"
                      alt="preview"
                    />
                    <span
                      class="text-xs text-n-teal-11 flex items-center gap-1"
                    >
                      <span class="i-lucide-check-circle size-3.5" />
                      {{
                        t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_IMAGE_UPLOADED'
                        )
                      }}
                    </span>
                  </div>
                  <label
                    class="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-dashed border-n-strong text-n-slate-11 hover:bg-n-solid-3 cursor-pointer transition-colors"
                  >
                    <span
                      v-if="isUploadingMedia"
                      class="i-lucide-loader-2 size-4 animate-spin"
                    />
                    <span v-else class="i-lucide-upload size-4" />
                    {{
                      isUploadingMedia
                        ? t(
                            'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_UPLOADING'
                          )
                        : form.headerImagePreview
                          ? t(
                              'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_IMAGE_CHANGE'
                            )
                          : t(
                              'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_IMAGE_PICK'
                            )
                    }}
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      class="hidden"
                      :disabled="isUploadingMedia"
                      @change="onMediaSelect"
                    />
                  </label>
                  <span class="text-xs text-n-slate-9">
                    {{
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_IMAGE_HINT')
                    }}
                  </span>
                </div>
              </div>

              <!-- Body -->
              <div v-if="!isAuth" class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_LABEL') }}
                  <span class="text-n-ruby-11">*</span>
                </label>

                <div
                  class="flex items-center gap-0.5 p-1 border border-b-0 rounded-t-lg bg-n-solid-3 border-n-weak flex-wrap"
                >
                  <button
                    type="button"
                    class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                    :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_BOLD')"
                    @click="formatBold"
                  >
                    <span class="i-lucide-bold size-4" />
                  </button>
                  <button
                    type="button"
                    class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                    :title="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_ITALIC')
                    "
                    @click="formatItalic"
                  >
                    <span class="i-lucide-italic size-4" />
                  </button>
                  <button
                    type="button"
                    class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                    :title="
                      t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_STRIKETHROUGH'
                      )
                    "
                    @click="formatStrike"
                  >
                    <span class="i-lucide-strikethrough size-4" />
                  </button>
                  <button
                    type="button"
                    class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                    :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_CODE')"
                    @click="formatCode"
                  >
                    <span class="i-lucide-code size-4" />
                  </button>
                  <span class="w-px h-4 bg-n-weak mx-0.5" />
                  <button
                    type="button"
                    class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                    :title="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_NEWLINE')
                    "
                    @click="insertNewline"
                  >
                    <span class="i-lucide-wrap-text size-4" />
                  </button>
                  <button
                    type="button"
                    class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                    :title="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.INSERT_VARIABLE')
                    "
                    @click="insertVariable"
                  >
                    <span class="i-lucide-braces size-4" />
                  </button>
                  <div class="relative">
                    <button
                      type="button"
                      class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                      :title="
                        t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_EMOJI')
                      "
                      @click="showEmojiPicker = !showEmojiPicker"
                    >
                      <span class="i-lucide-smile size-4" />
                    </button>
                    <div
                      v-if="showEmojiPicker"
                      class="absolute top-full left-0 mt-1 z-10 p-2 rounded-lg bg-n-solid-2 border border-n-weak shadow-lg grid grid-cols-8 gap-1 w-[220px]"
                    >
                      <button
                        v-for="emoji in COMMON_EMOJIS"
                        :key="emoji"
                        type="button"
                        class="p-1 rounded hover:bg-n-solid-3 text-base cursor-pointer transition-colors text-center leading-none"
                        @click="insertEmoji(emoji)"
                      >
                        {{ emoji }}
                      </button>
                    </div>
                  </div>
                  <span class="ml-auto text-xs text-n-slate-9 pr-1">
                    {{ bodyCharCount }}/{{ LIMITS.body }}
                  </span>
                </div>

                <textarea
                  ref="bodyTextarea"
                  v-model="form.body"
                  rows="5"
                  :maxlength="LIMITS.body"
                  class="px-3 py-2 text-sm border rounded-b-lg rounded-t-none bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand resize-none font-mono"
                  :placeholder="
                    t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_PLACEHOLDER')
                  "
                  @focus="showEmojiPicker = false"
                />
                <span class="text-xs text-n-slate-9">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_HINT') }}
                </span>
                <span v-if="formErrors.body" class="text-xs text-n-ruby-11">
                  {{ formErrors.body }}
                </span>
              </div>

              <!-- Variable examples -->
              <div v-if="detectedVariables.length" class="flex flex-col gap-2">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.VARIABLES_TITLE') }}
                </label>
                <div
                  v-for="(v, idx) in detectedVariables"
                  :key="v"
                  class="flex items-center gap-2"
                >
                  <span class="text-xs font-mono text-n-slate-11 w-12 shrink-0">
                    {{ varLabel(v) }}
                  </span>
                  <input
                    v-model="form.variableExamples[idx]"
                    type="text"
                    class="flex-1 px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8"
                    :placeholder="
                      t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.VARIABLE_EXAMPLE_PLACEHOLDER'
                      )
                    "
                  />
                </div>
              </div>

              <!-- Footer -->
              <div v-if="isStandard" class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER_LABEL') }}
                </label>
                <input
                  v-model="form.footer"
                  type="text"
                  :maxlength="LIMITS.footer"
                  class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
                  :placeholder="
                    t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER_PLACEHOLDER')
                  "
                />
                <span class="text-xs text-n-slate-9 self-end">
                  {{ footerCharCount }}/{{ LIMITS.footer }}
                </span>
              </div>

              <!-- Buttons -->
              <div v-if="isStandard" class="flex flex-col gap-2">
                <div class="flex items-center justify-between">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS_LABEL') }}
                  </label>
                  <button
                    v-if="form.buttons.length < 3"
                    class="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg text-n-brand hover:bg-n-brand-2 cursor-pointer transition-colors"
                    @click="addButton"
                  >
                    <span class="i-lucide-plus size-3" />
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.ADD_BUTTON') }}
                  </button>
                </div>

                <div
                  v-for="(btn, idx) in form.buttons"
                  :key="idx"
                  class="flex flex-col gap-2 p-3 border rounded-lg bg-n-solid-1 border-n-weak"
                >
                  <div class="flex items-center gap-2">
                    <select
                      v-model="btn.type"
                      class="px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 cursor-pointer"
                    >
                      <option
                        v-for="bt in BUTTON_TYPES"
                        :key="bt.value"
                        :value="bt.value"
                      >
                        {{ t(bt.label) }}
                      </option>
                    </select>
                    <button
                      class="ml-auto p-1 rounded text-n-slate-10 hover:text-n-ruby-11 cursor-pointer transition-colors"
                      @click="removeButton(idx)"
                    >
                      <span class="i-lucide-x size-4" />
                    </button>
                  </div>
                  <input
                    v-model="btn.text"
                    type="text"
                    :maxlength="LIMITS.button"
                    class="w-full px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8"
                    :placeholder="
                      t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_TEXT_PLACEHOLDER'
                      )
                    "
                  />
                  <input
                    v-if="btn.type === 'URL'"
                    v-model="btn.url"
                    type="text"
                    class="w-full px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8"
                    :placeholder="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.URL_PLACEHOLDER')
                    "
                  />
                  <input
                    v-if="btn.type === 'URL' && btn.url.includes('{{')"
                    v-model="btn.urlExample"
                    type="text"
                    class="w-full px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8"
                    :placeholder="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.URL_VAR_EXAMPLE')
                    "
                  />
                  <input
                    v-if="btn.type === 'PHONE_NUMBER'"
                    v-model="btn.phone"
                    type="text"
                    class="w-full px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8"
                    :placeholder="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.PHONE_PLACEHOLDER')
                    "
                  />
                </div>
              </div>

              <!-- Carousel: shared buttons + cards -->
              <template v-if="isCarousel">
                <!-- Shared buttons -->
                <div class="flex flex-col gap-2">
                  <div class="flex items-center justify-between">
                    <label class="text-sm font-medium text-n-slate-12">
                      {{
                        t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CAROUSEL_BUTTONS')
                      }}
                    </label>
                    <button
                      v-if="
                        form.carouselButtons.length < CAROUSEL_LIMITS.buttons
                      "
                      class="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg text-n-brand hover:bg-n-brand-2 cursor-pointer transition-colors"
                      @click="addCarouselButton"
                    >
                      <span class="i-lucide-plus size-3" />
                      {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.ADD_BUTTON') }}
                    </button>
                  </div>
                  <span class="text-xs text-n-slate-9">
                    {{
                      t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CAROUSEL_BUTTONS_HINT'
                      )
                    }}
                  </span>
                  <div
                    v-for="(btn, idx) in form.carouselButtons"
                    :key="`cbtn-${idx}`"
                    class="flex flex-col gap-2 p-3 border rounded-lg bg-n-solid-1 border-n-weak"
                  >
                    <div class="flex items-center gap-2">
                      <select
                        v-model="btn.type"
                        class="px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 cursor-pointer"
                      >
                        <option
                          v-for="bt in BUTTON_TYPES"
                          :key="bt.value"
                          :value="bt.value"
                        >
                          {{ t(bt.label) }}
                        </option>
                      </select>
                      <button
                        class="ml-auto p-1 rounded text-n-slate-10 hover:text-n-ruby-11 cursor-pointer transition-colors"
                        @click="removeCarouselButton(idx)"
                      >
                        <span class="i-lucide-x size-4" />
                      </button>
                    </div>
                    <input
                      v-model="btn.text"
                      type="text"
                      :maxlength="LIMITS.button"
                      class="w-full px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8"
                      :placeholder="
                        t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_TEXT_PLACEHOLDER'
                        )
                      "
                    />
                    <input
                      v-if="btn.type === 'URL'"
                      v-model="btn.url"
                      type="text"
                      class="w-full px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8"
                      :placeholder="
                        t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.URL_PLACEHOLDER')
                      "
                    />
                    <input
                      v-if="btn.type === 'PHONE_NUMBER'"
                      v-model="btn.phone"
                      type="text"
                      class="w-full px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8"
                      :placeholder="
                        t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.PHONE_PLACEHOLDER'
                        )
                      "
                    />
                  </div>
                </div>

                <!-- Cards -->
                <div class="flex flex-col gap-2">
                  <div class="flex items-center justify-between">
                    <label class="text-sm font-medium text-n-slate-12">
                      {{
                        t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CAROUSEL_CARDS')
                      }}
                      <span class="text-n-ruby-11">*</span>
                    </label>
                    <button
                      v-if="form.cards.length < CAROUSEL_LIMITS.cards"
                      class="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg text-n-brand hover:bg-n-brand-2 cursor-pointer transition-colors"
                      @click="addCard"
                    >
                      <span class="i-lucide-plus size-3" />
                      {{
                        t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CAROUSEL_ADD_CARD'
                        )
                      }}
                    </button>
                  </div>

                  <div
                    v-for="(card, idx) in form.cards"
                    :key="`card-${idx}`"
                    class="flex flex-col gap-2 p-3 border rounded-lg bg-n-solid-1 border-n-weak"
                  >
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-semibold text-n-slate-11">
                        {{
                          t(
                            'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CAROUSEL_CARD',
                            {
                              number: idx + 1,
                            }
                          )
                        }}
                      </span>
                      <button
                        class="p-1 rounded text-n-slate-10 hover:text-n-ruby-11 cursor-pointer transition-colors"
                        @click="removeCard(idx)"
                      >
                        <span class="i-lucide-trash-2 size-3.5" />
                      </button>
                    </div>
                    <div class="flex items-center gap-3">
                      <img
                        v-if="card.headerImagePreview"
                        :src="card.headerImagePreview"
                        class="object-cover rounded size-12"
                        alt="card"
                      />
                      <label
                        class="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-dashed cursor-pointer border-n-strong text-n-slate-11 hover:bg-n-solid-3 transition-colors"
                      >
                        <span class="i-lucide-upload size-3.5" />
                        {{
                          card.headerImagePreview
                            ? t(
                                'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_IMAGE_CHANGE'
                              )
                            : t(
                                'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_IMAGE_PICK'
                              )
                        }}
                        <input
                          type="file"
                          accept="image/jpeg,image/png"
                          class="hidden"
                          :disabled="isUploadingMedia"
                          @change="onCardMediaSelect(idx, $event)"
                        />
                      </label>
                    </div>
                    <textarea
                      v-model="card.body"
                      rows="2"
                      :maxlength="CAROUSEL_LIMITS.cardBody"
                      class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand resize-none"
                      :placeholder="
                        t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CAROUSEL_CARD_BODY_PLACEHOLDER'
                        )
                      "
                    />
                  </div>
                </div>
              </template>

              <!-- LTO: oferta por tempo limitado -->
              <template v-if="isLto">
                <div
                  class="flex flex-col gap-2 p-3 border rounded-lg bg-n-amber-2 border-n-amber-6"
                >
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LTO_TEXT_LABEL') }}
                  </label>
                  <input
                    v-model="form.lto.text"
                    type="text"
                    :maxlength="LTO_OFFER_LIMIT"
                    class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
                    :placeholder="
                      t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LTO_TEXT_PLACEHOLDER'
                      )
                    "
                  />
                  <span class="text-xs text-n-slate-9 self-end">
                    {{ form.lto.text.length }}/{{ LTO_OFFER_LIMIT }}
                  </span>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      v-model="form.lto.hasExpiration"
                      type="checkbox"
                      class="size-4 rounded border-n-slate-8 text-n-brand"
                    />
                    <span class="text-sm text-n-slate-12">
                      {{
                        t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LTO_HAS_EXPIRATION'
                        )
                      }}
                    </span>
                  </label>
                  <span class="text-xs text-n-slate-9">
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LTO_HINT') }}
                  </span>
                </div>

                <!-- Coupon code (COPY_CODE) -->
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LTO_CODE_LABEL') }}
                    <span class="text-n-ruby-11">*</span>
                  </label>
                  <input
                    v-model="form.lto.code"
                    type="text"
                    maxlength="15"
                    class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand font-mono"
                    :placeholder="
                      t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LTO_CODE_PLACEHOLDER'
                      )
                    "
                  />
                </div>

                <!-- URL button (required) -->
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LTO_URL_LABEL') }}
                    <span class="text-n-ruby-11">*</span>
                  </label>
                  <input
                    v-model="form.lto.urlText"
                    type="text"
                    :maxlength="LIMITS.button"
                    class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
                    :placeholder="
                      t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_TEXT_PLACEHOLDER'
                      )
                    "
                  />
                  <input
                    v-model="form.lto.url"
                    type="text"
                    class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
                    :placeholder="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.URL_PLACEHOLDER')
                    "
                  />
                </div>
              </template>

              <!-- Autenticação (OTP) -->
              <template v-if="isAuth">
                <p class="text-xs text-n-slate-10 -mt-1">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.AUTH_HINT') }}
                </p>
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.AUTH_EXPIRY_LABEL')
                    }}
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      v-model.number="form.auth.codeExpirationMinutes"
                      type="number"
                      min="1"
                      max="90"
                      class="w-24 px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
                    />
                    <span class="text-sm text-n-slate-11">
                      {{
                        t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.AUTH_EXPIRY_UNIT')
                      }}
                    </span>
                  </div>
                </div>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    v-model="form.auth.addSecurityRecommendation"
                    type="checkbox"
                    class="size-4 rounded border-n-slate-8 text-n-brand"
                  />
                  <span class="text-sm text-n-slate-12">
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.AUTH_SECURITY') }}
                  </span>
                </label>
              </template>

              <p v-if="formErrors.general" class="text-xs text-n-ruby-11">
                {{ formErrors.general }}
              </p>
            </div>

            <!-- PREVIEW column -->
            <div
              class="flex flex-col gap-3 p-5 border-l border-n-weak bg-n-solid-3 overflow-y-auto"
            >
              <span
                class="text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
              >
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.TITLE') }}
              </span>
              <div class="rounded-xl p-3 bg-[#e5ddd5] min-h-[200px]">
                <!-- Carousel preview -->
                <template v-if="isCarousel">
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <div
                    v-if="form.body"
                    class="max-w-[88%] mb-2 px-2.5 py-2 text-sm rounded-lg bg-white text-n-slate-12 whitespace-pre-wrap break-words leading-snug shadow-sm"
                    v-html="previewBodyHtml"
                  />
                  <div class="flex gap-2 overflow-x-auto pb-1">
                    <div
                      v-for="(card, idx) in form.cards"
                      :key="`prev-card-${idx}`"
                      class="w-40 shrink-0 rounded-lg shadow-sm overflow-hidden bg-white"
                    >
                      <img
                        v-if="card.headerImagePreview"
                        :src="card.headerImagePreview"
                        class="w-full h-24 object-cover"
                        alt="card"
                      />
                      <div
                        v-else
                        class="w-full h-24 flex items-center justify-center bg-n-slate-3"
                      >
                        <span class="i-lucide-image size-6 text-n-slate-8" />
                      </div>
                      <p
                        class="px-2 py-1.5 text-xs text-n-slate-12 break-words leading-snug"
                      >
                        {{
                          card.body ||
                          t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.EMPTY_BODY')
                        }}
                      </p>
                      <div
                        v-if="form.carouselButtons.length"
                        class="border-t border-n-weak"
                      >
                        <div
                          v-for="(btn, bidx) in form.carouselButtons"
                          :key="`prev-cbtn-${bidx}`"
                          class="flex items-center justify-center gap-1 py-1.5 text-xs font-medium border-t first:border-t-0 border-n-weak text-[#00a5f4]"
                        >
                          <span :class="buttonIcon(btn.type)" class="size-3" />
                          {{
                            btn.text ||
                            t(
                              'INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.BUTTON_PLACEHOLDER'
                            )
                          }}
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="!form.cards.length"
                      class="flex items-center justify-center w-40 h-32 shrink-0 rounded-lg border border-dashed border-n-slate-7 text-xs text-n-slate-9 text-center px-2"
                    >
                      {{
                        t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.CAROUSEL_EMPTY'
                        )
                      }}
                    </div>
                  </div>
                </template>

                <!-- LTO preview -->
                <div
                  v-else-if="isLto"
                  class="max-w-[85%] rounded-lg shadow-sm overflow-hidden bg-white"
                >
                  <div
                    class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold bg-n-amber-3 text-n-amber-11"
                  >
                    <span class="i-lucide-clock size-3.5" />
                    {{
                      form.lto.text ||
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LTO_TEXT_LABEL')
                    }}
                  </div>
                  <div class="px-2.5 py-2">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <p
                      v-if="form.body"
                      class="text-sm text-n-slate-12 whitespace-pre-wrap break-words leading-snug"
                      v-html="previewBodyHtml"
                    />
                    <p v-else class="text-sm text-n-slate-9 italic">
                      {{
                        t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.EMPTY_BODY')
                      }}
                    </p>
                  </div>
                  <div class="border-t border-n-weak">
                    <div
                      class="flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-[#00a5f4]"
                    >
                      <span class="i-lucide-copy size-3.5" />
                      {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.LTO_COPY') }}
                    </div>
                    <div
                      class="flex items-center justify-center gap-1.5 py-2 text-sm font-medium border-t border-n-weak text-[#00a5f4]"
                    >
                      <span class="i-lucide-external-link size-3.5" />
                      {{
                        form.lto.urlText ||
                        t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.BUTTON_PLACEHOLDER'
                        )
                      }}
                    </div>
                  </div>
                </div>

                <!-- Auth (OTP) preview -->
                <div
                  v-else-if="isAuth"
                  class="max-w-[85%] rounded-lg shadow-sm overflow-hidden bg-white"
                >
                  <div class="px-2.5 py-2">
                    <p class="text-sm text-n-slate-12">
                      {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.AUTH_BODY') }}
                    </p>
                    <p
                      v-if="form.auth.codeExpirationMinutes"
                      class="text-[11px] text-n-slate-9 mt-1.5"
                    >
                      {{
                        t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.AUTH_EXPIRY', {
                          minutes: form.auth.codeExpirationMinutes,
                        })
                      }}
                    </p>
                  </div>
                  <div class="border-t border-n-weak">
                    <div
                      class="flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-[#00a5f4]"
                    >
                      <span class="i-lucide-copy size-3.5" />
                      {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.AUTH_COPY') }}
                    </div>
                  </div>
                </div>

                <div
                  v-else
                  class="max-w-[85%] rounded-lg shadow-sm overflow-hidden bg-white"
                >
                  <!-- header media -->
                  <img
                    v-if="
                      form.headerFormat === 'IMAGE' && form.headerImagePreview
                    "
                    :src="form.headerImagePreview"
                    class="w-full h-32 object-cover"
                    alt="header"
                  />
                  <div
                    v-else-if="
                      ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerFormat)
                    "
                    class="w-full h-24 flex items-center justify-center bg-n-slate-3"
                  >
                    <span class="i-lucide-image size-7 text-n-slate-8" />
                  </div>

                  <div class="px-2.5 py-2">
                    <!-- header text -->
                    <p
                      v-if="form.headerFormat === 'TEXT' && form.header"
                      class="text-sm font-semibold text-n-slate-12 mb-1"
                      v-html="previewHeaderHtml"
                    />
                    <!-- body -->
                    <p
                      v-if="form.body"
                      class="text-sm text-n-slate-12 whitespace-pre-wrap break-words leading-snug"
                      v-html="previewBodyHtml"
                    />
                    <p v-else class="text-sm text-n-slate-9 italic">
                      {{
                        t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.EMPTY_BODY')
                      }}
                    </p>
                    <!-- footer -->
                    <p
                      v-if="form.footer"
                      class="text-[11px] text-n-slate-9 mt-1.5"
                    >
                      {{ form.footer }}
                    </p>
                  </div>

                  <!-- buttons -->
                  <div
                    v-if="form.buttons.length"
                    class="border-t border-n-weak"
                  >
                    <div
                      v-for="(btn, idx) in form.buttons"
                      :key="idx"
                      class="flex items-center justify-center gap-1.5 py-2 text-sm font-medium border-t first:border-t-0 border-n-weak text-[#00a5f4]"
                    >
                      <span :class="buttonIcon(btn.type)" class="size-3.5" />
                      {{
                        btn.text ||
                        t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.BUTTON_PLACEHOLDER'
                        )
                      }}
                    </div>
                  </div>
                </div>
              </div>
              <p class="text-xs text-n-slate-9">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.HINT') }}
              </p>
            </div>
          </div>

          <div
            class="flex items-center justify-end gap-3 p-5 border-t border-n-weak"
          >
            <Button
              :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CANCEL')"
              slate
              faded
              @click="showCreateModal = false"
            />
            <Button
              :label="
                editingId
                  ? t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SUBMIT_EDIT')
                  : t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SUBMIT')
              "
              icon="i-lucide-send"
              blue
              :is-loading="isCreating"
              @click="handleCreate"
            />
          </div>
        </div>
      </div>
    </teleport>

    <!-- View modal (read-only) -->
    <teleport to="body">
      <div
        v-if="viewTemplate"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
        @click.self="viewTemplate = null"
      >
        <div
          class="w-full max-w-md max-h-[90vh] flex flex-col rounded-2xl bg-n-solid-2 border border-n-weak shadow-2xl overflow-hidden"
        >
          <div
            class="flex items-center justify-between p-5 border-b border-n-weak"
          >
            <h3 class="text-base font-semibold text-n-slate-12 font-mono">
              {{ viewData?.name }}
            </h3>
            <button
              class="p-1 rounded-lg text-n-slate-10 hover:bg-n-solid-3 cursor-pointer transition-colors"
              @click="viewTemplate = null"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>
          <div v-if="viewData" class="p-5 overflow-y-auto bg-n-solid-3">
            <!-- Carousel view -->
            <div v-if="viewData.isCarousel" class="rounded-xl p-3 bg-[#e5ddd5]">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div
                v-if="viewData.body"
                class="max-w-[88%] mb-2 px-2.5 py-2 text-sm rounded-lg bg-white text-n-slate-12 whitespace-pre-wrap break-words leading-snug shadow-sm"
                v-html="renderWhatsappText(viewData.body, viewData.varExamples)"
              />
              <div class="flex gap-2 overflow-x-auto pb-1">
                <div
                  v-for="(card, idx) in viewData.cards"
                  :key="`view-card-${idx}`"
                  class="w-40 shrink-0 rounded-lg shadow-sm overflow-hidden bg-white"
                >
                  <div
                    class="w-full h-24 flex items-center justify-center bg-n-slate-3"
                  >
                    <span class="i-lucide-image size-6 text-n-slate-8" />
                  </div>
                  <p
                    class="px-2 py-1.5 text-xs text-n-slate-12 break-words leading-snug"
                  >
                    {{ card.body }}
                  </p>
                  <div
                    v-if="card.buttons.length"
                    class="border-t border-n-weak"
                  >
                    <div
                      v-for="(btn, bidx) in card.buttons"
                      :key="`view-cbtn-${bidx}`"
                      class="flex items-center justify-center gap-1 py-1.5 text-xs font-medium border-t first:border-t-0 border-n-weak text-[#00a5f4]"
                    >
                      <span :class="buttonIcon(btn.type)" class="size-3" />
                      {{ btn.text }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- LTO view -->
            <div v-else-if="viewData.isLto" class="rounded-xl p-3 bg-[#e5ddd5]">
              <div
                class="max-w-[85%] rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <div
                  class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold bg-n-amber-3 text-n-amber-11"
                >
                  <span class="i-lucide-clock size-3.5" />
                  {{ viewData.ltoText }}
                </div>
                <div class="px-2.5 py-2">
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <p
                    class="text-sm text-n-slate-12 whitespace-pre-wrap break-words leading-snug"
                    v-html="
                      renderWhatsappText(viewData.body, viewData.varExamples)
                    "
                  />
                </div>
                <div
                  v-if="viewData.ltoButtons.length"
                  class="border-t border-n-weak"
                >
                  <div
                    v-for="(btn, idx) in viewData.ltoButtons"
                    :key="`view-lto-${idx}`"
                    class="flex items-center justify-center gap-1.5 py-2 text-sm font-medium border-t first:border-t-0 border-n-weak text-[#00a5f4]"
                  >
                    <span
                      :class="
                        btn.type === 'COPY_CODE'
                          ? 'i-lucide-copy'
                          : 'i-lucide-external-link'
                      "
                      class="size-3.5"
                    />
                    {{ btn.text }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Auth (OTP) view -->
            <div
              v-else-if="viewData.isAuth"
              class="rounded-xl p-3 bg-[#e5ddd5]"
            >
              <div
                class="max-w-[85%] rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <div class="px-2.5 py-2">
                  <p class="text-sm text-n-slate-12">
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.AUTH_BODY') }}
                  </p>
                  <p
                    v-if="viewData.authExpiration"
                    class="text-[11px] text-n-slate-9 mt-1.5"
                  >
                    {{
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.AUTH_EXPIRY', {
                        minutes: viewData.authExpiration,
                      })
                    }}
                  </p>
                </div>
                <div class="border-t border-n-weak">
                  <div
                    class="flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-[#00a5f4]"
                  >
                    <span class="i-lucide-copy size-3.5" />
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.AUTH_COPY') }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Standard view -->
            <div v-else class="rounded-xl p-3 bg-[#e5ddd5]">
              <div
                class="max-w-[85%] rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <div
                  v-if="
                    ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(
                      viewData.headerFormat
                    )
                  "
                  class="w-full h-24 flex items-center justify-center bg-n-slate-3"
                >
                  <span class="i-lucide-image size-7 text-n-slate-8" />
                </div>
                <div class="px-2.5 py-2">
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <p
                    v-if="
                      viewData.headerFormat === 'TEXT' && viewData.headerText
                    "
                    class="text-sm font-semibold text-n-slate-12 mb-1"
                    v-html="
                      renderWhatsappText(
                        viewData.headerText,
                        viewData.varExamples
                      )
                    "
                  />
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <p
                    class="text-sm text-n-slate-12 whitespace-pre-wrap break-words leading-snug"
                    v-html="
                      renderWhatsappText(viewData.body, viewData.varExamples)
                    "
                  />
                  <p
                    v-if="viewData.footer"
                    class="text-[11px] text-n-slate-9 mt-1.5"
                  >
                    {{ viewData.footer }}
                  </p>
                </div>
                <div
                  v-if="viewData.buttons.length"
                  class="border-t border-n-weak"
                >
                  <div
                    v-for="(btn, idx) in viewData.buttons"
                    :key="idx"
                    class="flex items-center justify-center gap-1.5 py-2 text-sm font-medium border-t first:border-t-0 border-n-weak text-[#00a5f4]"
                  >
                    <span :class="buttonIcon(btn.type)" class="size-3.5" />
                    {{ btn.text }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="flex items-center justify-end gap-3 p-5 border-t border-n-weak"
          >
            <Button
              v-if="viewTemplate && isEditable(viewTemplate)"
              :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.EDIT')"
              icon="i-lucide-pencil"
              blue
              faded
              @click="
                openEditModal(viewTemplate);
                viewTemplate = null;
              "
            />
            <Button
              :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CANCEL')"
              slate
              faded
              @click="viewTemplate = null"
            />
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>
